import type { SeatKind, SeatsStateDTO } from "../data/seatsApi";

/**
 * What the team screen leaves behind for the workspace preview's "Who has
 * access" section — same pattern, and same reason, as ZOHO_CONNECTION_KEY:
 * the preview needs it on every screen, without every screen re-reading the
 * roster.
 *
 * The client-wide role and work-area catalogs that used to live here are gone
 * with the module-wise maker–checker rewrite: access is now a seat on a module
 * tab, and the tab owns the permissions. What survives is this answer's shape,
 * because OnboardingLayout's preview and the go-live summary both read it.
 */

export const TEAM_PREVIEW_KEY = "teamAccess";

export interface TeamPreviewEntry {
  name: string;
  /** The badge: "Checker" if they check anywhere, else "Maker". */
  roleLabel: string;
  /** "Sales (Maker) · Journal (Checker)" — already worded for the preview. */
  line: string;
}

/**
 * The seated people, one entry each, in roster order.
 *
 * Only people with a seat appear: with roles gone, "has access" means "sits on
 * a tab", and a roster row on its own grants nothing. Somebody who checks
 * anywhere is badged Checker — the wider of the two seats, since a Checker can
 * do everything a Maker on the same tab can.
 */
export function toTeamPreview(state: SeatsStateDTO): TeamPreviewEntry[] {
  const entries: TeamPreviewEntry[] = [];

  for (const member of state.members) {
    // Ordered by the tab strip, not by the order the seats were written, so
    // two people's lines read the same way round.
    const held = state.tabs
      .map((tab) => ({ label: tab.label, seat: member.seats[tab.key] }))
      .filter((entry): entry is { label: string; seat: SeatKind } => Boolean(entry.seat));

    if (held.length === 0) continue;

    entries.push({
      name: member.name,
      roleLabel: held.some((entry) => entry.seat === "checker") ? "Checker" : "Maker",
      line: held.map((entry) => `${entry.label} (${seatLabel(entry.seat)})`).join(" · "),
    });
  }

  return entries;
}

/** "Maker" / "Checker" — the seat vocabulary, capitalised the way the screen says it. */
export function seatLabel(seat: SeatKind): string {
  return seat === "checker" ? "Checker" : "Maker";
}
